import { Alert, Box, Button, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { Service } from "../api/client";
import { deleteService } from "../api/management";
import { Panel } from "../components/layout/Panel";
import { CompGrid, type Column } from "../components/grid/CompGrid";
import { Link as MuiLink } from "@mui/material";
import { useHierarchyContext } from "../contexts/HierarchyContext";

export function PageProduct() {
  const { product } = useParams<{ product: string }>();
  const productSlug = product ?? null;
  const { services: allServices, loading, error } = useHierarchyContext();
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);

  const services = useMemo(
    () => (productSlug ? allServices.filter((s) => s.productSlug === productSlug) : []),
    [allServices, productSlug],
  );

  if (!productSlug) {
    return <Alert severity="warning">Missing product in URL. Please navigate from Home.</Alert>;
  }

  const columns: Column<Service>[] = [
    {
      id: "slug",
      label: "Slug",
      sortable: true,
      sortValue: (s) => s.slug,
      render: (s) => (
        <MuiLink
          component={RouterLink}
          to={`/services/${encodeURIComponent(s.slug)}/health`}
          underline="hover"
        >
          <Typography variant="body2" className="om-text-mono">
            {s.slug}
          </Typography>
        </MuiLink>
      ),
    },
    {
      id: "name",
      label: "Name",
      sortable: true,
      sortValue: (s) => s.name,
      render: (s) => <Typography variant="body2">{s.name}</Typography>,
    },
    {
      id: "description",
      label: "Description",
      render: (s) => (
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          {s.description || "-"}
        </Typography>
      ),
    },
    {
      id: "actions",
      label: (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mx: 4 }}>
          <Button
            size="small"
            variant="text"
            component={RouterLink}
            disabled={loading || !!error}
            to={`/products/${encodeURIComponent(productSlug)}/services/new`}
          >
            Create
          </Button>
        </Box>
      ),
      render: (s) => (
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mx: 4 }}>
          <Button
            size="small"
            variant="text"
            component={RouterLink}
            to={`/services/${encodeURIComponent(s.slug)}/health`}
          >
            Open
          </Button>
          <Button
            size="small"
            variant="text"
            color="error"
            disabled={loading || !!error}
            onClick={async () => {
              if (!window.confirm(`Delete service "${s.name || s.slug}"? This will also delete its health checks, alerts, and channels.`)) return;
              setActionError(null);
              try {
                await deleteService(s.slug, undefined);
                await queryClient.refetchQueries({ queryKey: ["hierarchy", "full"] });
              } catch (err) {
                setActionError(err instanceof Error ? err.message : "Failed to delete service");
              }
            }}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {actionError && (
        <Alert severity="error" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      <Panel title="Services">
        <CompGrid
          items={services}
          loading={loading}
          error={error?.message ?? null}
          emptyMessage="No services yet."
          columns={columns}
          keyExtractor={(s) => s.slug}
        />
      </Panel>
    </Box>
  );
}
